import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../constants/resolutionMap';
import { generateCompleteRoomDesign, shouldChargeForError, getErrorMessage } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import { useProjects } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import AngleViewDropdown from '../../components/AngleViewDropdown';
import RoomTypeDropdown from '../../components/RoomTypeDropdown';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import { setGeneratedImageDragData } from '../../utils/dragDropUtils';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';

const CompleteRoomDesign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Upload states
  const [venueImages, setVenueImages] = useState([]);
  const [furnitureMaterial, setFurnitureMaterial] = useState([]);
  const [surfacesMaterial, setSurfacesMaterial] = useState([]);

  // Asset library state
  const [libraryAssets, setLibraryAssets] = useState([]);

  // Room config
  const [roomType, setRoomType] = useState('--');
  const [clearRoomBeforeDesigning, setClearRoomBeforeDesigning] = useState(false);

  // Controls (right side)
  const [commonAngleView, setCommonAngleView] = useState('frontal');
  const [selectedAngleView, setSelectedAngleView] = useState('--');

  const handleToggleAngleView = () => {
    if (commonAngleView === 'frontal') {
      setCommonAngleView('topdown');
      setSelectedAngleView('Top-Down Flat View (90°)');
    } else {
      setCommonAngleView('frontal');
      setSelectedAngleView('Straight-On Eye-Level View');
    }
  };

  const handleAngleViewChange = (val) => {
    setSelectedAngleView(val);
    if (val === 'Straight-On Eye-Level View') {
      setCommonAngleView('frontal');
    } else if (val === 'Top-Down Flat View (90°)') {
      setCommonAngleView('topdown');
    } else {
      setCommonAngleView(null);
    }
  };
  const [selectedImageSize, setSelectedImageSize] = useState('Auto');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [promptText, setPromptText] = useState('');
  const promptRef = useRef(null);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionMobileRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();
  const MAX_PROMPT_LENGTH = 1600;
  const MAX_TOTAL_UPLOADS = 6;

  // Virtual Tool Guide
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const totalGuideSteps = 14;

  // Guide refs - desktop
  const venueUploadDesktopRef = useRef(null);
  const furnitureUploadDesktopRef = useRef(null);
  const surfacesUploadDesktopRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  const furnitureEditDesktopRef = useRef(null);
  const angleToggleDesktopRef = useRef(null);
  const roomTypeDesktopRef = useRef(null);
  const angleViewDesktopRef = useRef(null);
  const modelDesktopRef = useRef(null);
  const aspectRatioDesktopRef = useRef(null);
  const resolutionDesktopRef = useRef(null);
  const promptDesktopRef = useRef(null);
  const generateBtnDesktopRef = useRef(null);
  const resultDesktopRef = useRef(null);

  // Guide refs - mobile
  const venueUploadMobileRef = useRef(null);
  const furnitureUploadMobileRef = useRef(null);
  const surfacesUploadMobileRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);
  const furnitureEditMobileRef = useRef(null);
  const angleToggleMobileRef = useRef(null);
  const roomTypeMobileRef = useRef(null);
  const angleViewMobileRef = useRef(null);
  const modelMobileRef = useRef(null);
  const aspectRatioMobileGuideRef = useRef(null);
  const resolutionMobileGuideRef = useRef(null);
  const promptMobileRef = useRef(null);
  const generateBtnMobileRef = useRef(null);
  const resultMobileRef = useRef(null);

  const scrollGuideTarget = (step) => {
    const desktopRefs = [venueUploadDesktopRef, furnitureUploadDesktopRef, surfacesUploadDesktopRef, assetLibraryDesktopRef, furnitureEditDesktopRef, angleToggleDesktopRef, roomTypeDesktopRef, angleViewDesktopRef, modelDesktopRef, aspectRatioDesktopRef, resolutionDesktopRef, promptDesktopRef, generateBtnDesktopRef, resultDesktopRef];
    const mobileRefs = [venueUploadMobileRef, furnitureUploadMobileRef, surfacesUploadMobileRef, assetLibraryMobileRef, furnitureEditMobileRef, angleToggleMobileRef, roomTypeMobileRef, angleViewMobileRef, modelMobileRef, aspectRatioMobileGuideRef, resolutionMobileGuideRef, promptMobileRef, generateBtnMobileRef, resultMobileRef];
    const ref = window.innerWidth >= 768 ? desktopRefs[step - 1]?.current : mobileRefs[step - 1]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const openGuide = () => {
    setGuideStep(1);
    setGuideVisible(false);
    setGuideAnimating(false);
    setShowGuide(true);
    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setGuideVisible(true));
      });
    }, 80);
  };

  const nextGuideStep = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep >= 4;
    if (needsScroll) {
      scrollGuideTarget(nextStep);
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 400);
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setGuideVisible(true));
        });
      }
    }, 600);
  };

  const closeGuide = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
      setGuideAnimating(false);
    }, 700);
  };

  // Generation
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);

  const roomTypes = ['--', 'Living room', 'Bedroom', 'Meeting room', 'Bathroom', 'Cabinet', 'Kitchen', 'Reception', 'Lounge', 'Bar', 'Terrace', 'Co-working area', 'Restaurant'];

  // Models constant removed - using shared ModelSelectionDropdown component


  // Total uploads across all 3 categories
  const totalUploads = venueImages.length + furnitureMaterial.length + surfacesMaterial.length;
  const remainingUploads = MAX_TOTAL_UPLOADS - totalUploads;

  // Drag and drop states
  const [isDraggingVenue, setIsDraggingVenue] = useState(false);
  const [isDraggingFurniture, setIsDraggingFurniture] = useState(false);
  const [isDraggingSurfaces, setIsDraggingSurfaces] = useState(false);

  const handleVenueUpload = async (files) => {
    const fileArray = Array.isArray(files) ? files : Array.from(files || []);
    const allowed = fileArray.slice(0, remainingUploads);
    if (allowed.length === 0) return;
    const imageUrls = (await Promise.all(allowed.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    setVenueImages((prev) => [...prev, ...imageUrls]);
  };

  const handleMaterialUpload = async (files, type) => {
    const fileArray = Array.isArray(files) ? files : Array.from(files || []);
    const allowed = fileArray.slice(0, remainingUploads);
    if (allowed.length === 0) return;
    const imageUrls = (await Promise.all(allowed.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    if (type === 'furniture') {
      setFurnitureMaterial((prev) => [...prev, ...imageUrls]);
    } else if (type === 'surfaces') {
      setSurfacesMaterial((prev) => [...prev, ...imageUrls]);
    }
  };

  // Drag and drop handlers for venue
  const handleVenueDragOver = (e) => {
    e.preventDefault();
    setIsDraggingVenue(true);
  };

  const handleVenueDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingVenue(false);
  };

  const handleVenueDrop = (e) => {
    e.preventDefault();
    setIsDraggingVenue(false);
    // Check for asset library drop (URL data)
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      const totalCurrent = venueImages.length + furnitureMaterial.length + surfacesMaterial.length;
      if (totalCurrent >= MAX_TOTAL_UPLOADS) return;
      setVenueImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      return;
    }
    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/') && ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)
    );
    if (files.length > 0) {
      handleVenueUpload(files);
    }
  };

  // Drag and drop handlers for furniture
  const handleFurnitureDragOver = (e) => {
    e.preventDefault();
    setIsDraggingFurniture(true);
  };

  const handleFurnitureDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingFurniture(false);
  };

  const handleFurnitureDrop = (e) => {
    e.preventDefault();
    setIsDraggingFurniture(false);
    // Check for asset library drop (URL data)
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      const totalCurrent = venueImages.length + furnitureMaterial.length + surfacesMaterial.length;
      if (totalCurrent >= MAX_TOTAL_UPLOADS) return;
      setFurnitureMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      return;
    }
    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/') && ['image/png', 'image/jpeg'].includes(file.type)
    );
    if (files.length > 0) {
      handleMaterialUpload(files, 'furniture');
    }
  };

  // Drag and drop handlers for surfaces
  const handleSurfacesDragOver = (e) => {
    e.preventDefault();
    setIsDraggingSurfaces(true);
  };

  const handleSurfacesDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingSurfaces(false);
  };

  const handleSurfacesDrop = (e) => {
    e.preventDefault();
    setIsDraggingSurfaces(false);
    // Check for asset library drop (URL data)
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      const totalCurrent = venueImages.length + furnitureMaterial.length + surfacesMaterial.length;
      if (totalCurrent >= MAX_TOTAL_UPLOADS) return;
      setSurfacesMaterial((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      return;
    }
    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/') && ['image/png', 'image/jpeg'].includes(file.type)
    );
    if (files.length > 0) {
      handleMaterialUpload(files, 'surfaces');
    }
  };

  const handleDragStart = (e, imageUrl, type) => {
    if (!imageUrl || !type) return;
    try {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', imageUrl);
      e.dataTransfer.setData('imageUrl', imageUrl);
      e.dataTransfer.setData('imageType', type);
    } catch (error) {
      console.error('Drag error:', error);
    }
  };

  const handleAssetDrop = (e, targetSetter) => {
    e.preventDefault();
    try {
      const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
      if (!imageUrl) return;
      const totalCurrent = venueImages.length + furnitureMaterial.length + surfacesMaterial.length;
      if (totalCurrent >= MAX_TOTAL_UPLOADS) return;
      targetSetter((prev) => {
        if (!prev.includes(imageUrl)) return [...prev, imageUrl];
        return prev;
      });
    } catch (error) {
      console.error('Drop error:', error);
    }
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    // Validate required uploads
    if (venueImages.length === 0) {
      toast.error('Please upload at least one venue/room image.');
      isGeneratingRef.current = false;
      return;
    }

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, getModelCreditCost(selectedModel));
    if (!creditCheck?.allowed) {
      toast.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    abortControllerRef.current = new AbortController();

    try {
      setIsGenerating(true);
      startTimer();
      setGeneratedImage(null);

      // Get selected aspect ratio dimensions
      const dimensions = getResolutionDimensions(selectedResolution);

      // Build complete-room-design system prompt
      const toolSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini 3.1 Flash Image Preview image generation model for ground-up interior design visualization. Apply professional interior design composition logic — spatial harmony, material coordination, proportional furniture placement, functional object analysis, and considered colour palette integration — while strictly using only the references provided by the user. Do not invent, add, or creatively expand beyond what is literally present in the uploaded reference images.

Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.

Intro: /complete-room-design is a complete interior design suite intended to compose a fully furnished room, hospitality space, or venue from separate individual props, furniture pieces, objects, and surface materials uploaded by the user. The user uploads a reference venue image that defines the spatial envelope, then uploads separate furniture items and surface materials through dedicated upload windows. The AI must integrate all separately uploaded elements into the venue as a cohesive, professionally resolved interior composition. Every object, material, and attribute uploaded through the furniture and surfaces upload windows must be present in the final generation — nothing omitted, nothing substituted. The venue image defines the architectural shell; the separately uploaded references define everything that goes inside it.

Task: First, analyze the venue image precisely — identify its spatial volume, architectural features, natural light sources, wall positions, window placements, ceiling height, floor area, colour palette, and overall spatial character. Second, analyze each separately uploaded furniture piece and surface material reference — for every object, determine its primary function in the human world: is it a functional piece (seating, storage, work surface, lighting fixture) or a decorative element (art, ornament, accent piece, textile accessory)? Understanding each object's purpose determines its logical placement within the room. Third, compose the final interior scene by integrating all uploaded objects and materials into the venue with professional spatial reasoning: functional pieces are placed where they serve their purpose (seating faces conversation zones or focal points, work surfaces receive natural light, storage aligns with walls), decorative elements are placed where they complement the composition (art at eye level on appropriate walls, accent pieces on surfaces that benefit from visual layering, textiles where they add warmth or acoustic value). Every placement decision must have a spatial or functional rationale — no object is placed arbitrarily. The result must be a hyper-realistic render that looks like a professionally designed interior where every element belongs intentionally. The room composition must be logically and aesthetically appealing and coherent.

High-quality keywords to maintain in every generation: complete interior design composition, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera, professionally composed interior, harmonious material palette, intentional object placement

Temperature: 1 (default Gemini).

INPUTS RECEIVED:
- venueImages: ${venueImages?.length} image(s) uploaded (REQUIRED) — the room or venue reference image uploaded by the user. This defines the exact spatial envelope: wall positions, ceiling height, floor area, window placements, door positions, architectural features, natural light direction, colour palette, and room proportions. The venue must remain identical and must not be altered at any cost — its spatial volume, architectural character, colour palette, material finishes, and all structural characteristics must be preserved precisely in the output.
- clearRoomBeforeDesigning: ${clearRoomBeforeDesigning} — boolean toggle. ${clearRoomBeforeDesigning ? 'TRUE: disregard ALL existing furniture, objects, decorative elements, and movable items visible inside the uploaded venue image. Treat the venue as if it were an empty shell — preserve only the architectural envelope (walls, floor, ceiling, windows, doors, built-in elements, fixed architectural features). Focus solely on integrating the objects and materials uploaded through the furniture and surfaces upload windows.' : 'FALSE (default): retain the existing furniture and objects visible in the venue image as part of the scene, and integrate the additionally uploaded furniture and surface references alongside them into a cohesive composition.'}
- furnitureMaterial: ${furnitureMaterial?.length} image(s) uploaded (REQUIRED) — furniture and object reference image(s) uploaded separately by the user through the dedicated furniture upload window. These define the exact furniture pieces, fixtures, and objects to be placed inside the venue. For each uploaded piece, analyze its primary function: seating, storage, work surface, lighting, dining, sleeping, or decorative/accent. Place each piece according to its functional purpose — not randomly, but with spatial reasoning that reflects how an experienced interior designer would position it.
${surfacesMaterial?.length > 0 ? `- surfacesMaterial: ${surfacesMaterial?.length} image(s) uploaded — surface, textile, and material reference image(s) uploaded separately by the user through the dedicated surfaces upload window (carpets, curtains, upholstery, wall finishes, decorative textiles). Extract the exact textile patterns, material finishes, colour tones, and surface qualities from these references. Apply them to the appropriate corresponding surfaces within the room.` : '- surfacesMaterial: No surface materials uploaded. Generate the scene using only the venue and furniture references without adding textile or surface elements.'}
${promptText ? `- promptText: "${promptText}" — include the user's text literally, word for word.` : '- promptText: No additional user text provided.'}
${roomType && roomType !== '--' ? `- roomType: "${roomType}" — include as programmatic and spatial context only. This confirms the space's function so the furniture arrangement respects functional logic appropriate to that space type.` : '- roomType: No specific room type provided.'}
${commonAngleView ? `- commonAngleView: "${commonAngleView}" — ${commonAngleView === 'frontal' ? 'render the designed room as a frontal camera perspective view' : 'render as a top-down overhead plan view'}.` : ''}
${selectedAngleView && selectedAngleView !== '--' ? `- selectedAngleView: "${selectedAngleView}" — additional view specification. This takes priority over commonAngleView as the more specific instruction.` : ''}
${selectedImageSize && selectedImageSize !== 'Auto' ? `- selectedImageSize: "${selectedImageSize}" — extract ratio strictly from what the user selected in the UI.` : ''}

STRICT RULES:
- The venue is absolute — never alter wall positions, window placements, door locations, ceiling height, architectural features, colour palette, material finishes, spatial volume, or room proportions from the venue image.
- Do not invent furniture, objects, materials, textiles, decorative elements, plants, light fixtures, or any item not visible in the user's uploaded reference images.
- Every uploaded furniture piece and object must appear in the final generation. Nothing uploaded through the furniture or surfaces windows may be omitted.
- Do not substitute or reinterpret referenced furniture or materials.
- Every object must be placed with functional and spatial reasoning.
- Materials must be applied to their corresponding surface types.
- Do not use any style labels unless the user explicitly wrote them in promptText.
- If any input is missing or '--', skip it silently without generating filler content.

Generate the complete interior design composition now with ALL uploaded elements integrated professionally.`;

      let result;

      if (isFluxModel(selectedModel)) {
        result = await generateWithFlux({
          modelName: selectedModel,
          userPromptText: promptText,
          referenceImages: venueImages,
          moodboardImages: [...(furnitureMaterial || []), ...(surfacesMaterial || [])],
          roomType: roomType === 'Custom' ? '' : roomType,
          selectedAngleShot: selectedAngleView || commonAngleView,
          imageSize: selectedImageSize,
          signal: abortControllerRef.current.signal,
        });
      } else {
        result = await generateCompleteRoomDesign({
          venueImages,
          clearRoomBeforeDesigning,
          furnitureMaterial,
          surfacesMaterial,
          promptText,
          roomType,
          commonAngleView,
          selectedAngleView,
          selectedImageSize,
          selectedModel,
          dimensions,
          toolSystemPrompt,
          signal: abortControllerRef.current.signal,
        });
      }

      clearTimer();
      setGeneratedImage(result);
      setLibraryAssets((prev) => [...prev, result]);
      setSessionLibraryImages((prev) => [...prev, result]);
      toast.success('Image generated successfully!');
      showSaveModal(result, location.pathname);

      // Save to database
      await saveGenerationToDatabase(result, promptText || '', currentUser);
      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));
    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('cancelled')) {
        setCancelled();
      } else {
        console.error('Generation error:', error);
        setFailed();
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const saveGenerationToDatabase = async (imageUrl, prompt, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `${roomType || 'Complete Room'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: prompt,
          thumbnail: imageUrl,
          toolType: 'complete-room-design',
          toolName: 'Complete Room Design',
          status: 'completed',
          tags: [roomType, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt,
        isFavorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        modelUsed: selectedModel,
        imageSize: selectedImageSize,
        roomType: (roomType === 'Custom' ? '' : roomType) || 'Room',
        angleView: selectedAngleView || '--',
        style: 'Complete Room Design',
        resolution: selectedImageSize,
        cameraView: selectedAngleView || '--',
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...venueImages, ...(furnitureMaterial || []), ...(surfacesMaterial || [])],
        additionalParams: {
          prompt: promptText,
          commonAngleView,
          clearRoomBeforeDesigning,
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;

    try {
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `complete-room-design-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleDelete = () => {
    if (!generatedImage) return;
    setGeneratedImage(null);
  };

  const handleRegenerate = () => {
    if (!generatedImage) return;
    handleGenerate();
  };

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setGeneratedImage(designs[0].imageUrl);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setRoomType(metadata.roomType);
          if (metadata.angleView && metadata.angleView !== '--') setSelectedAngleView(metadata.angleView);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          if (metadata?.additionalParams?.prompt) setPromptText(metadata.additionalParams.prompt);
          if (metadata?.additionalParams?.commonAngleView) setCommonAngleView(metadata.additionalParams.commonAngleView);
          if (metadata?.additionalParams?.clearRoomBeforeDesigning != null) setClearRoomBeforeDesigning(metadata.additionalParams.clearRoomBeforeDesigning);
        }
        if (designs[0]?.prompt && !promptText) setPromptText(designs[0].prompt);
      }
    };
    loadProjectData();
  }, []);

  useEffect(() => {
    if (promptRef?.current) {
      promptRef.current.style.height = 'auto';
      promptRef.current.style.height = promptRef.current.scrollHeight + 'px';
    }
  }, [promptText]);

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize] || resolutionMap['1:1'];
    return ratioMap ? ratioMap[resKey] || { width: 1024, height: 1024 } : { width: 1024, height: 1024 };
  };

  // Close aspect ratio dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktopAR = aspectRatioRef.current && aspectRatioRef.current.contains(event.target);
      const inMobileAR = aspectRatioMobileRef.current && aspectRatioMobileRef.current.contains(event.target);
      if (!inDesktopAR && !inMobileAR) {
        setIsAspectRatioOpen(false);
      }
      const inDesktopRes = resolutionRef.current && resolutionRef.current.contains(event.target);
      const inMobileRes = resolutionMobileRef.current && resolutionMobileRef.current.contains(event.target);
      if (!inDesktopRes && !inMobileRes) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Helmet>
        <title>Ground-Up Complete Room Design - Roomwork.ai</title>
        <meta
          name="description"
          content="Design complete rooms from scratch with comprehensive control over every element" />
      </Helmet>
      <div className="min-h-screen bg-black overflow-x-hidden">
        <Header />
        <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
          <div className="max-w-[1400px] mx-auto">
            {/* Header Section */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>

              <div className="flex items-start justify-between gap-4">
                <h1 className="text-3xl font-bold text-white hidden md:block">Ground-Up Complete Room Design</h1>
                <h1 className="text-xl font-bold text-white md:hidden">Ground-Up Complete Room Design</h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openGuide()}
                    className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                    style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                  >
                    <Icon name="HelpCircle" size={18} />
                    Virtual Tool Guide
                  </button>
                  <button
                    onClick={() => openGuide()}
                    className="md:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                    style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                  >
                    <Icon name="HelpCircle" size={16} />
                    Guide
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform whitespace-nowrap flex-shrink-0">
                    <Icon name="History" size={18} />
                    View Projects
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex gap-6">
              {/* Left Column (1/3) */}
              <div className="w-[30%] flex-shrink-0 space-y-6">

                {/* Uploads Card - Venue + Furniture + Surfaces */}
                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${totalUploads >= MAX_TOTAL_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                    {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                  </span>
                </div>
                <div className="glassmorphic-card rounded-3xl p-6 space-y-4">

                  {/* Step 1: Upload Room / Venue */}
                  <div ref={venueUploadDesktopRef} style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, padding: showGuide && guideStep === 1 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 1 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Upload Room / Venue<span className="text-red-500">*</span></label>
                    <label className={`block ${remainingUploads <= 0 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg"
                        className="hidden"
                        onChange={(e) => handleVenueUpload(e.target.files)}
                      />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl p-8 flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${
                          isDraggingVenue
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-orange-500/40'
                        }`}
                        onDragOver={handleVenueDragOver}
                        onDragLeave={handleVenueDragLeave}
                        onDrop={handleVenueDrop}
                      >
                        <Icon name="ImagePlus" size={36} className="text-orange-500 mb-2" />
                        <p className="text-white/70 text-xs">Upload image PNG or JPG</p>
                        <p className="text-white/50 text-[10px] mt-1">or drag and drop here</p>
                      </div>
                    </label>
                    {venueImages.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {venueImages.map((img, idx) => (
                          <UploadedImageThumb key={idx} src={img} alt={`Venue ${idx + 1}`} onRemove={() => setVenueImages((prev) => prev.filter((_, i) => i !== idx))} className="" imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                        ))}
                      </div>
                    )}
                    {/* Clear room toggle */}
                    <div className="flex items-center justify-between mt-3">
                      <span className={`text-xs ${clearRoomBeforeDesigning ? 'text-white' : 'text-white/50'}`}>Clear room before designing</span>
                      <button
                        onClick={() => setClearRoomBeforeDesigning(!clearRoomBeforeDesigning)}
                        className={`relative w-10 h-5 rounded-full transition-colors duration-300 flex-shrink-0 ${clearRoomBeforeDesigning ? 'bg-orange-500' : 'bg-white/20'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ${clearRoomBeforeDesigning ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Step 2: Furniture Material Upload */}
                  <div ref={furnitureUploadDesktopRef} style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, padding: showGuide && guideStep === 2 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 2 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Furniture</label>
                    <label className={`block ${remainingUploads <= 0 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleMaterialUpload(e.target.files, 'furniture')}
                        className="hidden"
                      />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl p-4 text-center border-2 border-dashed transition-all duration-300 ${
                          isDraggingFurniture
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-orange-500/40'
                        }`}
                        onDragOver={handleFurnitureDragOver}
                        onDragLeave={handleFurnitureDragLeave}
                        onDrop={handleFurnitureDrop}
                      >
                        <Icon name="Upload" size={24} className="text-orange-500 mx-auto mb-1" />
                        <p className="text-white/70 text-xs">Upload material image</p>
                        <p className="text-white/50 text-[10px] mt-1">or drag and drop here</p>
                      </div>
                    </label>
                    {furnitureMaterial?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {furnitureMaterial.map((img, idx) => (
                          <UploadedImageThumb key={idx} src={img} alt={`Furniture material ${idx + 1}`} onRemove={() => setFurnitureMaterial((prev) => prev.filter((_, i) => i !== idx))} className="" imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Step 3: Surfaces Material Upload */}
                  <div ref={surfacesUploadDesktopRef} style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, padding: showGuide && guideStep === 3 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 3 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Surfaces (carpets, curtains, upholstery)</label>
                    <label className={`block ${remainingUploads <= 0 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleMaterialUpload(e.target.files, 'surfaces')}
                        className="hidden"
                      />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl p-4 text-center border-2 border-dashed transition-all duration-300 ${
                          isDraggingSurfaces
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-orange-500/40'
                        }`}
                        onDragOver={handleSurfacesDragOver}
                        onDragLeave={handleSurfacesDragLeave}
                        onDrop={handleSurfacesDrop}
                      >
                        <Icon name="Upload" size={24} className="text-orange-500 mx-auto mb-1" />
                        <p className="text-white/70 text-xs">Upload material image</p>
                        <p className="text-white/50 text-[10px] mt-1">or drag and drop here</p>
                      </div>
                    </label>
                    {surfacesMaterial?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {surfacesMaterial.map((img, idx) => (
                          <UploadedImageThumb key={idx} src={img} alt={`Surfaces material ${idx + 1}`} onRemove={() => setSurfacesMaterial((prev) => prev.filter((_, i) => i !== idx))} className="" imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 4: Asset Library */}
                <div ref={assetLibraryDesktopRef} style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, padding: showGuide && guideStep === 4 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 4 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  generatedImages={sessionLibraryImages}
                  setGeneratedImages={setSessionLibraryImages}
                  currentProjectId={currentProjectId}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setGeneratedImage(remaining[0] || null);
                  }}
                />
                </div>

                {/* Step 5: Link to Furniture Editing */}
                <div ref={furnitureEditDesktopRef} style={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, padding: showGuide && guideStep === 5 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 5 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <button
                  onClick={() => navigate('/furniture-editing')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 transition-all duration-300 group"
                >
                  <Icon name="ArrowRight" size={14} className="text-orange-500 group-hover:translate-x-0.5 transition-transform duration-200" />
                  <span className="text-xs text-white/70 group-hover:text-white/90 transition-colors">Continue precise furniture editing</span>
                </button>
                </div>

              </div>

              {/* Right Column (2/3) */}
              <div className="w-[70%] flex-shrink-0 space-y-6">

                {/* Step 14: Result Window */}
                <div ref={resultDesktopRef} className="glassmorphic-card rounded-3xl p-6" style={{ position: showGuide && guideStep === 14 ? 'relative' : undefined, zIndex: showGuide && guideStep === 14 ? 201 : undefined, boxShadow: showGuide && guideStep === 14 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : undefined, transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-[12.6px] font-medium text-white">Result</h3>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-black/40 border-2 border-dashed border-white/20 h-[400px] flex items-center justify-center">
                    {generatedImage ? (
                      <img
                        src={generatedImage}
                        alt="Generated result"
                        className="w-full h-full object-contain cursor-grab active:cursor-grabbing"
                        draggable
                        onDragStart={(e) => setGeneratedImageDragData(e, generatedImage)}
                        style={{ aspectRatio: selectedImageSize === 'Auto' ? '1/1' : selectedImageSize.replace(':', '/') }}
                      />
                    ) : isGenerating ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                          <p className="text-white/70 text-sm">Generating your image...</p>
                          <p className="text-white/50 text-xs mt-2">This may take a few moments</p>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                          <p className="text-white/50 text-sm">No image generated yet</p>
                          <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    {generatedImage && (
                      <div className="absolute bottom-4 right-4 flex items-center space-x-2">
                        <button
                          onClick={handleDownload}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Download">
                          <Icon name="Download" size={18} className="text-white" />
                        </button>
                        <button
                          onClick={handleDelete}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Delete">
                          <Icon name="Trash2" size={18} className="text-white" />
                        </button>
                        <button
                          onClick={handleRegenerate}
                          className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Regenerate">
                          <Icon name="RefreshCw" size={18} className="text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Settings Panel - Merged */}
                <div className="glassmorphic-card rounded-3xl p-6 space-y-4">

                  {/* Step 6: Common Angle Views + Step 7: Room Type - Side by Side */}
                  <div className="grid grid-cols-2 gap-4">
                    <div ref={angleToggleDesktopRef} style={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, padding: showGuide && guideStep === 6 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 6 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      <label className="text-white/70 text-xs mb-3 block">Common angle views</label>
                      <div className="flex items-center justify-start gap-2">
                        <span className={`text-xs transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>
                          Frontal view
                        </span>
                        <button
                          onClick={handleToggleAngleView}
                          className={`relative w-14 h-7 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
                          <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${commonAngleView === 'frontal' ? 'left-0.5' : 'right-0.5'}`} />
                        </button>
                        <span className={`text-xs transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>
                          Top-down view
                        </span>
                      </div>
                    </div>
                    <div ref={roomTypeDesktopRef} style={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, padding: showGuide && guideStep === 7 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 7 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <RoomTypeDropdown
                      value={roomType}
                      onChange={(val) => {
                        setRoomType(val);
                        if (val === 'Custom') {
                          setTimeout(() => {
                            if (promptRef?.current) {
                              promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              promptRef.current.focus();
                            }
                          }, 80);
                        }
                      }}
                      showCustomOption
                    />
                    </div>
                  </div>

                  {/* Step 8: Angle View Dropdown */}
                  <div ref={angleViewDesktopRef} style={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, padding: showGuide && guideStep === 8 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 8 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <AngleViewDropdown value={selectedAngleView} onChange={handleAngleViewChange} />
                  </div>

                  {/* Step 9: Model Selection, Aspect Ratio & Resolution */}
                  <div className="grid grid-cols-2 gap-4">
                    <div ref={modelDesktopRef} style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, padding: showGuide && guideStep === 9 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 9 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      <ModelSelectionDropdown
                        value={selectedModel}
                        onChange={setSelectedModel}
                        excludeFlux
                      />
                    </div>

                    {/* Step 10: Aspect Ratio */}
                    <div ref={(node) => { aspectRatioRef.current = node; aspectRatioDesktopRef.current = node; }} style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, padding: showGuide && guideStep === 10 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 10 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                      <div className="relative">
                        <button
                          onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                          className="w-full glassmorphic-select rounded-2xl px-4 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all min-h-[46px]">
                          <div className="flex items-center gap-3">
                            <AspectRatioIcon
                              ratio={selectedImageSize}
                              size={18}
                              className="text-orange-500"
                            />
                            <span className="text-white text-sm">{selectedImageSize}</span>
                          </div>
                          <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                        </button>
                        {isAspectRatioOpen && (
                          <div
                            className="absolute z-10 w-full bottom-full mb-2 rounded-2xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                            style={{ backgroundColor: 'rgb(0, 0, 0)', backdropFilter: 'none' }}
                            onMouseDown={(e) => e.preventDefault()}>
                            {aspectRatios?.map((ratio) => (
                              <button
                                key={ratio?.value}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedImageSize(ratio?.value);
                                  setIsAspectRatioOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                                  selectedImageSize === ratio?.value
                                    ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                    : 'hover:bg-white/5'
                                }`}>
                                <div className="flex items-center gap-3">
                                  <AspectRatioIcon
                                    ratio={ratio?.value}
                                    size={18}
                                    className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'}
                                  />
                                  <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                    {ratio?.label}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Step 11: Resolution */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div ref={(node) => { resolutionRef.current = node; resolutionDesktopRef.current = node; }} style={{ position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, padding: showGuide && guideStep === 11 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 11 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                      <div className="relative">
                        <button
                          onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                          className={`w-full glassmorphic-select rounded-2xl px-4 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all min-h-[46px]`}>
                          <span className="text-white text-sm font-medium">{selectedResolution}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-xs">
                                {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                              </span>
                            <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                          </div>
                        </button>
                        {isResolutionOpen && (
                          <div
                            className="absolute z-10 w-full bottom-full mb-2 rounded-2xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                            style={{ backgroundColor: 'rgb(0, 0, 0)', backdropFilter: 'none' }}
                            onMouseDown={(e) => e.preventDefault()}>
                            {RESOLUTION_OPTIONS.map((resKey) => {
                              const dims = getResolutionDimensions(resKey);
                              return (
                                <button
                                  key={resKey}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedResolution(resKey);
                                    setIsResolutionOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                                    selectedResolution === resKey
                                      ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                      : 'hover:bg-white/5'
                                  }`}>
                                  <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                    {resKey}
                                  </span>
                                  <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                      {dims.width}×{dims.height}
                                    </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 12: Prompt */}
                  <div ref={promptDesktopRef} style={{ position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, padding: showGuide && guideStep === 12 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 12 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <label className="text-white/70 text-xs mb-2 block">
                      Prompt: ({promptText.length}/{MAX_PROMPT_LENGTH})
                    </label>
                    {roomType === 'Custom' && (
                      <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                    )}
                    <textarea
                      ref={promptRef}
                      value={promptText}
                      onChange={(e) => {
                        if (e.target.value.length <= MAX_PROMPT_LENGTH) setPromptText(e.target.value);
                      }}
                      placeholder={roomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request (optional)'}
                      rows={5}
                      className={`w-full glassmorphic-input rounded-2xl px-4 py-3 text-white bg-white/5 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] ${roomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                    />
                  </div>
                </div>

                {/* Step 13: Generate Button */}
                <div ref={generateBtnDesktopRef} style={{ position: showGuide && guideStep === 13 ? 'relative' : undefined, zIndex: showGuide && guideStep === 13 ? 201 : undefined, padding: showGuide && guideStep === 13 && guideVisible ? '10px' : undefined, margin: showGuide && guideStep === 13 && guideVisible ? '-10px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 13 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100">
                  <Icon name={isGenerating ? 'Loader' : 'Sparkles'} size={18} className={isGenerating ? 'animate-spin' : ''} />
                  {isGenerating ? 'Generating...' : 'Generate Image'}
                </button>
                </div>
                {isGenerating && (
                  <button
                    onClick={handleCancelGeneration}
                    className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                  >
                    <Icon name="X" size={16} />
                    Cancel
                  </button>
                )}

                <p className="text-white/50 text-xs text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden space-y-4">

              {/* Step 14: Result - Mobile */}
              <div ref={resultMobileRef} className="glassmorphic-card rounded-3xl p-5" style={{ position: showGuide && guideStep === 14 ? 'relative' : undefined, zIndex: showGuide && guideStep === 14 ? 201 : undefined, boxShadow: showGuide && guideStep === 14 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : undefined, transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <h3 className="text-white text-xs font-medium mb-4">Result</h3>
                <div className="relative rounded-2xl overflow-hidden bg-black/40 border-2 border-dashed border-white/20 min-h-[250px] flex items-center justify-center">
                  {generatedImage ? (
                    <img
                      src={generatedImage}
                      alt="Generated result"
                      className="max-w-full max-h-full cursor-grab active:cursor-grabbing"
                      draggable
                      onDragStart={(e) => setGeneratedImageDragData(e, generatedImage)}
                      style={{ aspectRatio: selectedImageSize === 'Auto' ? '1/1' : selectedImageSize.replace(':', '/') }}
                    />
                  ) : isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                        <p className="text-white/70 text-sm">Generating...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                        <p className="text-white/50 text-xs">No image generated yet</p>
                        <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                      </div>
                    </div>
                  )}
                  {generatedImage && (
                    <div className="absolute bottom-2 right-2 flex gap-1.5">
                      <button onClick={handleDownload} className="bg-black/70 hover:bg-black/80 rounded-lg p-2 transition-all border border-white/30" title="Download">
                        <Icon name="Download" size={14} className="text-white" />
                      </button>
                      <button onClick={handleDelete} className="bg-black/70 hover:bg-black/80 rounded-lg p-2 transition-all border border-white/30" title="Delete">
                        <Icon name="Trash2" size={14} className="text-white" />
                      </button>
                      <button onClick={handleRegenerate} className="bg-black/70 hover:bg-black/80 rounded-lg p-2 transition-all border border-white/30" title="Regenerate">
                        <Icon name="RefreshCw" size={14} className="text-white" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Uploads Card - Venue + Furniture + Surfaces */}
              <div className="text-left mb-2">
                <span className={`text-sm font-medium ${totalUploads >= MAX_TOTAL_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                  {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                </span>
              </div>
              <div className="glassmorphic-card rounded-3xl p-4 space-y-3">

                {/* Step 1: Upload Room / Venue - Mobile */}
                <div ref={venueUploadMobileRef} style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, padding: showGuide && guideStep === 1 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 1 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Upload Room / Venue<span className="text-red-500">*</span></label>
                  <label className={`block ${remainingUploads <= 0 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      onChange={(e) => handleVenueUpload(e.target.files)}
                    />
                    <div
                      className={`glassmorphic-upload-zone rounded-2xl h-[120px] flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${
                        isDraggingVenue
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/20 hover:border-orange-500/40'
                      }`}
                      onDragOver={handleVenueDragOver}
                      onDragLeave={handleVenueDragLeave}
                      onDrop={handleVenueDrop}
                    >
                      <Icon name="ImagePlus" size={32} className="text-orange-500 mb-2" />
                      <p className="text-white/70 text-xs">Upload image PNG or JPG</p>
                      <p className="text-white/50 text-[10px] mt-1">or drag and drop</p>
                    </div>
                  </label>
                  {venueImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {venueImages.map((img, idx) => (
                        <UploadedImageThumb key={idx} src={img} alt={`Venue ${idx + 1}`} onRemove={() => setVenueImages((prev) => prev.filter((_, i) => i !== idx))} className="" imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      ))}
                    </div>
                  )}
                  {/* Clear room toggle */}
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs ${clearRoomBeforeDesigning ? 'text-white' : 'text-white/50'}`}>Clear room before designing</span>
                    <button
                      onClick={() => setClearRoomBeforeDesigning(!clearRoomBeforeDesigning)}
                      className={`relative w-10 h-5 rounded-full transition-colors duration-300 flex-shrink-0 ${clearRoomBeforeDesigning ? 'bg-orange-500' : 'bg-white/20'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ${clearRoomBeforeDesigning ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {/* Step 2: Furniture - Mobile */}
                <div ref={furnitureUploadMobileRef} style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, padding: showGuide && guideStep === 2 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 2 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Furniture</label>
                  <label className={`block ${remainingUploads <= 0 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleMaterialUpload(e.target.files, 'furniture')}
                      className="hidden"
                    />
                    <div
                      className={`glassmorphic-upload-zone rounded-2xl p-3 text-center border-2 border-dashed transition-all duration-300 ${
                        isDraggingFurniture
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/20 hover:border-orange-500/40'
                      }`}
                      onDragOver={handleFurnitureDragOver}
                      onDragLeave={handleFurnitureDragLeave}
                      onDrop={handleFurnitureDrop}
                    >
                      <Icon name="Upload" size={20} className="text-orange-500 mx-auto mb-1" />
                      <p className="text-white/70 text-xs">Upload material image</p>
                      <p className="text-white/50 text-[9px] mt-1">or drag and drop</p>
                    </div>
                  </label>
                  {furnitureMaterial?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {furnitureMaterial.map((img, idx) => (
                        <UploadedImageThumb key={idx} src={img} alt={`Furniture ${idx + 1}`} onRemove={() => setFurnitureMaterial((prev) => prev.filter((_, i) => i !== idx))} className="" imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      ))}
                    </div>
                  )}
                </div>

                {/* Step 3: Surfaces - Mobile */}
                <div ref={surfacesUploadMobileRef} style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, padding: showGuide && guideStep === 3 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 3 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-[12.6px] mb-2 block">Surfaces (carpets, curtains, upholstery)</label>
                  <label className={`block ${remainingUploads <= 0 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleMaterialUpload(e.target.files, 'surfaces')}
                      className="hidden"
                    />
                    <div
                      className={`glassmorphic-upload-zone rounded-2xl p-3 text-center border-2 border-dashed transition-all duration-300 ${
                        isDraggingSurfaces
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/20 hover:border-orange-500/40'
                      }`}
                      onDragOver={handleSurfacesDragOver}
                      onDragLeave={handleSurfacesDragLeave}
                      onDrop={handleSurfacesDrop}
                    >
                      <Icon name="Upload" size={20} className="text-orange-500 mx-auto mb-1" />
                      <p className="text-white/70 text-xs">Upload material image</p>
                      <p className="text-white/50 text-[9px] mt-1">or drag and drop</p>
                    </div>
                  </label>
                  {surfacesMaterial?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {surfacesMaterial.map((img, idx) => (
                        <UploadedImageThumb key={idx} src={img} alt={`Surfaces ${idx + 1}`} onRemove={() => setSurfacesMaterial((prev) => prev.filter((_, i) => i !== idx))} className="" imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Step 4: Asset Library - Mobile */}
              <div ref={assetLibraryMobileRef} style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, padding: showGuide && guideStep === 4 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 4 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <AssetLibraryPanel
                libraryAssets={libraryAssets}
                setLibraryAssets={setLibraryAssets}
                generatedImages={sessionLibraryImages}
                setGeneratedImages={setSessionLibraryImages}
                currentProjectId={currentProjectId}
                onImageDeleted={(remaining) => {
                  if (!isGenerating) setGeneratedImage(remaining[0] || null);
                }}
              />
              </div>

              {/* Step 5: Link to Furniture Editing - Mobile */}
              <div ref={furnitureEditMobileRef} style={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, padding: showGuide && guideStep === 5 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 5 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <button
                onClick={() => navigate('/furniture-editing')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 transition-all duration-300 group"
              >
                <Icon name="ArrowRight" size={14} className="text-orange-500 group-hover:translate-x-0.5 transition-transform duration-200" />
                <span className="text-xs text-white/70 group-hover:text-white/90 transition-colors">Continue precise furniture editing</span>
              </button>
              </div>

              {/* Controls - Mobile */}
              <div className="glassmorphic-card rounded-3xl p-4 space-y-3">
                {/* Step 6: Common Angle Views - Mobile */}
                <div ref={angleToggleMobileRef} style={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, padding: showGuide && guideStep === 6 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 6 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-xs mb-2 block">Common angle views</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>Frontal view</span>
                    <button
                      onClick={handleToggleAngleView}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${commonAngleView === 'frontal' ? 'left-0.5' : 'right-0.5'}`} />
                    </button>
                    <span className={`text-[10px] ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>Top-down view</span>
                  </div>
                </div>
                {/* Step 7: Room Type - Mobile */}
                <div ref={roomTypeMobileRef} style={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, padding: showGuide && guideStep === 7 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 7 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <RoomTypeDropdown
                  value={roomType}
                  onChange={(val) => {
                    setRoomType(val);
                    if (val === 'Custom') {
                      setTimeout(() => {
                        const mobileTextarea = promptMobileRef.current?.querySelector('textarea');
                        if (mobileTextarea) {
                          mobileTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          mobileTextarea.focus();
                        }
                      }, 80);
                    }
                  }}
                  mobile
                  showCustomOption
                />
                </div>
                {/* Step 8: Angle View - Mobile */}
                <div ref={angleViewMobileRef} style={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, padding: showGuide && guideStep === 8 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 8 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <AngleViewDropdown value={selectedAngleView} onChange={handleAngleViewChange} />
                </div>
                {/* Step 9: Model - Mobile */}
                <div ref={modelMobileRef} style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, padding: showGuide && guideStep === 9 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 9 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <ModelSelectionDropdown
                    value={selectedModel}
                    onChange={setSelectedModel}
                    mobile
                    excludeFlux
                  />
                </div>
                {/* Step 10: Aspect Ratio - Mobile */}
                <div ref={(node) => { aspectRatioMobileRef.current = node; aspectRatioMobileGuideRef.current = node; }} style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, padding: showGuide && guideStep === 10 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 10 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                  <div className="relative">
                    <button
                      onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                      className="w-full glassmorphic-select rounded-xl px-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all min-h-[38px]">
                      <div className="flex items-center gap-2">
                        <AspectRatioIcon
                          ratio={selectedImageSize}
                          size={16}
                          className="text-orange-500"
                        />
                        <span className="text-white text-xs">{selectedImageSize}</span>
                      </div>
                      <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                    </button>
                    {isAspectRatioOpen && (
                      <div
                        className="absolute z-10 w-full bottom-full mb-2 rounded-xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                        style={{ backgroundColor: 'rgb(0, 0, 0)', backdropFilter: 'none' }}
                        onMouseDown={(e) => e.preventDefault()}>
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.value}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedImageSize(ratio.value);
                              setIsAspectRatioOpen(false);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                              selectedImageSize === ratio.value
                                ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                : 'hover:bg-white/5'
                            }`}>
                            <div className="flex items-center gap-2">
                              <AspectRatioIcon
                                ratio={ratio.value}
                                size={16}
                                className={selectedImageSize === ratio.value ? 'text-orange-500' : 'text-white/50'}
                              />
                              <span className={`text-xs ${selectedImageSize === ratio.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                {ratio.label}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Step 11: Resolution - Mobile */}
                <div ref={(node) => { resolutionMobileRef.current = node; resolutionMobileGuideRef.current = node; }} style={{ position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, padding: showGuide && guideStep === 11 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 11 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                  <div className="relative">
                    <button
                      onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                      className={`w-full glassmorphic-select rounded-xl px-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all min-h-[38px]`}>
                      <span className="text-white text-xs font-medium">{selectedResolution}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-white/50 text-xs">
                            {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                          </span>
                        <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                      </div>
                    </button>
                    {isResolutionOpen && (
                      <div
                        className="absolute z-10 w-full bottom-full mb-2 rounded-xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                        style={{ backgroundColor: 'rgb(0, 0, 0)', backdropFilter: 'none' }}
                        onMouseDown={(e) => e.preventDefault()}>
                        {RESOLUTION_OPTIONS.map((resKey) => {
                          const dims = getResolutionDimensions(resKey);
                          return (
                            <button
                              key={resKey}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedResolution(resKey);
                                setIsResolutionOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                                selectedResolution === resKey
                                  ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                  : 'hover:bg-white/5'
                              }`}>
                              <span className={`text-xs ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                {resKey}
                              </span>
                              <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                  {dims.width}×{dims.height}
                                </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {/* Step 12: Prompt - Mobile */}
                <div ref={promptMobileRef} style={{ position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, padding: showGuide && guideStep === 12 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 12 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <label className="text-white/70 text-xs mb-2 block">Prompt: ({promptText.length}/{MAX_PROMPT_LENGTH})</label>
                  {roomType === 'Custom' && (
                    <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                  )}
                  <textarea
                    value={promptText}
                    onChange={(e) => { if (e.target.value.length <= MAX_PROMPT_LENGTH) setPromptText(e.target.value); }}
                    placeholder={roomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request (optional)'}
                    rows={3}
                    className={`w-full glassmorphic-input rounded-xl px-3 py-2 text-sm text-white bg-white/5 focus:outline-none placeholder:text-white/30 resize-none ${roomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                  />
                </div>
              </div>

              {/* Step 13: Generate - Mobile */}
              <div ref={generateBtnMobileRef} style={{ position: showGuide && guideStep === 13 ? 'relative' : undefined, zIndex: showGuide && guideStep === 13 ? 201 : undefined, padding: showGuide && guideStep === 13 && guideVisible ? '8px' : undefined, margin: showGuide && guideStep === 13 && guideVisible ? '-8px' : undefined, borderRadius: '16px', boxShadow: showGuide && guideStep === 13 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <button onClick={handleGenerate} disabled={isGenerating} className="w-full glassmorphic-button rounded-xl py-2.5 text-sm text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100">
                <Icon name={isGenerating ? 'Loader' : 'Sparkles'} size={16} className={isGenerating ? 'animate-spin' : ''} />
                {isGenerating ? 'Generating...' : 'Generate Image'}
              </button>
              </div>
              {isGenerating && (
                <button
                  onClick={handleCancelGeneration}
                  className="w-full glassmorphic-button rounded-xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                >
                  <Icon name="X" size={16} />
                  Cancel
                </button>
              )}
              <p className="text-white/50 text-[10px] text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </main>
      </div>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
          }
        }}
      />

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
        <div
          className="fixed inset-0 z-[200]"
          onClick={closeGuide}
        >
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        </div>

        {/* Tooltip bubble */}
        <div
          className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
          style={(() => {
            const desktopRefs = [venueUploadDesktopRef.current, furnitureUploadDesktopRef.current, surfacesUploadDesktopRef.current, assetLibraryDesktopRef.current, furnitureEditDesktopRef.current, angleToggleDesktopRef.current, roomTypeDesktopRef.current, angleViewDesktopRef.current, modelDesktopRef.current, aspectRatioDesktopRef.current, resolutionDesktopRef.current, promptDesktopRef.current, generateBtnDesktopRef.current, resultDesktopRef.current];
            const mobileRefs = [venueUploadMobileRef.current, furnitureUploadMobileRef.current, surfacesUploadMobileRef.current, assetLibraryMobileRef.current, furnitureEditMobileRef.current, angleToggleMobileRef.current, roomTypeMobileRef.current, angleViewMobileRef.current, modelMobileRef.current, aspectRatioMobileGuideRef.current, resolutionMobileGuideRef.current, promptMobileRef.current, generateBtnMobileRef.current, resultMobileRef.current];
            const ref = window.innerWidth >= 768 ? desktopRefs[guideStep - 1] : mobileRefs[guideStep - 1];
            if (!ref) return { top: '50%', left: '50%' };
            const rect = ref.getBoundingClientRect();
            const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
            // Steps 6+ are on the right column → bubble appears to the left (except step 5 which is left column)
            if (guideStep >= 6) {
              return {
                top: rect.top + rect.height / 2,
                left: rect.left - bubbleWidth - 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            return {
              top: rect.top + rect.height / 2,
              left: rect.right + 32,
              opacity: guideVisible ? 1 : 0,
              transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
              transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            };
          })()}
        >
          <div className="pointer-events-auto">
            {/* Arrow - points right for steps 6-9, left for steps 1-5 */}
            {guideStep >= 6 ? (
              <>
                <div
                  className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            ) : (
              <>
            <div
              className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
              style={{
                borderTop: '10px solid transparent',
                borderBottom: '10px solid transparent',
                borderRight: '10px solid rgba(254, 131, 56, 0.3)',
                opacity: guideVisible ? 1 : 0,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
              }}
            />
            <div
              className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
              style={{
                borderTop: '9px solid transparent',
                borderBottom: '9px solid transparent',
                borderRight: '9px solid rgba(30, 30, 30, 0.95)',
                opacity: guideVisible ? 1 : 0,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
              }}
            />
              </>
            )}
            <div
              className="rounded-2xl p-5 text-center"
              style={{
                background: 'rgb(24, 24, 24)',
                border: '1px solid rgba(254, 131, 56, 0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)',
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'scale(1)' : 'scale(0.97)',
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            >
              <div
                className="flex items-center justify-center gap-2 mb-2"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                }}
              >
                <Icon name={[null, "ImagePlus", "Armchair", "Layers", "FolderOpen", "ArrowRight", "ToggleRight", "Home", "Video", "Cpu", "RectangleHorizontal", "ScanLine", "Type", "Sparkles", "Monitor"][guideStep] || "Settings"} size={18} className="text-white/50" />
                <span className="text-sm font-semibold text-white">Step {guideStep}</span>
              </div>
              <p
                className="text-sm text-white/80 leading-relaxed"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s',
                }}
              >
                {guideStep === 1 ? (
                  <>Upload the <span className="text-white font-medium italic">room or venue reference image</span> that defines the spatial envelope — wall positions, ceiling height, floor area, windows, and architectural features. Use the toggle below to <span className="text-white font-medium italic">clear existing furniture</span> before designing, or keep it to build upon what's already in the room.</>
                ) : guideStep === 2 ? (
                  <>Upload <span className="text-white font-medium italic">furniture reference images</span> — individual pieces such as sofas, tables, chairs, lamps, and fixtures. Each uploaded piece will be analyzed and placed inside the venue with professional spatial reasoning based on its function.</>
                ) : guideStep === 3 ? (
                  <>Upload <span className="text-white font-medium italic">surface and textile materials</span> — carpets, curtains, upholstery fabrics, wall finishes. The AI will extract their patterns, colours, and textures and apply them to the corresponding surfaces within the room composition.</>
                ) : guideStep === 4 ? (
                  <>Generated images which you chose to store inside Roomwork.ai can be <span className="text-white font-medium italic">dragged and dropped</span> into 'Upload Room / Venue', 'Furniture', or 'Surfaces' upload zones.</>
                ) : guideStep === 5 ? (
                  <>Once the full room composition looks good but you want to <span className="text-white font-medium italic">fine-tune specific furniture pieces or surface details</span>, use this shortcut to continue in the Furniture & Materials Editing tool for precise adjustments.</>
                ) : guideStep === 6 ? (
                  <>A shortcut toggle button which allows you to quickly choose between a <span className="text-white font-medium italic">frontal view</span> and a <span className="text-white font-medium italic">top-down aerial view</span> for your room generation.</>
                ) : guideStep === 7 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">type of room</span> you want to generate.</>
                ) : guideStep === 8 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">camera angle perspective</span> for your room generation.</>
                ) : guideStep === 9 ? (
                  <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                ) : guideStep === 10 ? (
                  <>Image ratio: vertical or horizontal (choose the precise ratio according to the dropdown menu icons). Different <span className="text-white font-medium italic">ratios</span> allow you to explore different room compositions.</>
                ) : guideStep === 11 ? (
                  <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                ) : guideStep === 12 ? (
                  <>An optional text field for additional instructions. Describe specific <span className="text-white font-medium italic">styles, colours, materials, or arrangements</span> you want the AI to follow when composing the room.</>
                ) : guideStep === 13 ? (
                  <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you're ready! Choose only the fields that are relevant to your needs.</>
                ) : guideStep === 14 ? (
                  <>Receive the generated image in the <span className="text-white font-medium italic">'Result'</span> window. Use the buttons in the bottom right corner of the Result window to <span className="text-white font-medium italic">download</span>, <span className="text-white font-medium italic">delete</span>, or <span className="text-white font-medium italic">regenerate</span> the image using the exact same settings.</>
                ) : null}
              </p>
              <button
                onClick={() => guideStep < totalGuideSteps ? nextGuideStep() : closeGuide()}
                className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
                }}
              >
                {guideStep < totalGuideSteps ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );
};

export default CompleteRoomDesign;
